import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  BookOpen,
  Play,
  CheckCircle,
  Clock,
  Filter,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { enrollmentsAPI, coursesAPI } from '../lib/api';
import { toast } from 'sonner';

export default function MyLearningPage() {
  const [loading, setLoading] = useState(true);
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const enrollmentsRes = await enrollmentsAPI.getMy();
      setEnrollments(enrollmentsRes.data);

      // Fetch course details for each enrollment
      const coursePromises = enrollmentsRes.data.map((e) =>
        coursesAPI.getById(e.course_id).then((res) => ({ [e.course_id]: res.data }))
      );
      const courseResults = await Promise.all(coursePromises);
      const coursesMap = courseResults.reduce((acc, curr) => ({ ...acc, ...curr }), {});
      setCourses(coursesMap);
    } catch (error) {
      toast.error('Failed to load your courses');
    } finally {
      setLoading(false);
    }
  };

  const inProgress = enrollments.filter((e) => !e.completed_at);
  const completed = enrollments.filter((e) => e.completed_at);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="my-learning">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-sketch text-4xl text-ink">My Learning</h1>
            <p className="text-muted-foreground mt-1">
              Track your progress across all enrolled courses
            </p>
          </div>
          <Link to="/courses">
            <Button className="btn-sketchy" data-testid="browse-more-btn">
              Browse More Courses
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid sm:grid-cols-3 gap-6">
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Enrolled</p>
                <p className="font-sketch text-3xl text-ink">{enrollments.length}</p>
              </div>
            </div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">In Progress</p>
                <p className="font-sketch text-3xl text-ink">{inProgress.length}</p>
              </div>
            </div>
          </div>
          <div className="stats-card">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Completed</p>
                <p className="font-sketch text-3xl text-ink">{completed.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="in-progress">
          <TabsList className="bg-transparent border-b-2 border-black rounded-none h-auto p-0 mb-6">
            <TabsTrigger
              value="in-progress"
              className="tab-sketchy data-[state=active]:active"
              data-testid="in-progress-tab"
            >
              In Progress ({inProgress.length})
            </TabsTrigger>
            <TabsTrigger
              value="completed"
              className="tab-sketchy data-[state=active]:active"
              data-testid="completed-tab"
            >
              Completed ({completed.length})
            </TabsTrigger>
            <TabsTrigger
              value="all"
              className="tab-sketchy data-[state=active]:active"
              data-testid="all-tab"
            >
              All ({enrollments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="in-progress">
            <EnrollmentList enrollments={inProgress} courses={courses} showContinue />
          </TabsContent>

          <TabsContent value="completed">
            <EnrollmentList enrollments={completed} courses={courses} />
          </TabsContent>

          <TabsContent value="all">
            <EnrollmentList enrollments={enrollments} courses={courses} showContinue />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}

function EnrollmentList({ enrollments, courses, showContinue = false }) {
  if (enrollments.length === 0) {
    return (
      <div className="card-sketchy text-center py-16">
        <BookOpen className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
        <h3 className="font-sketch text-2xl text-ink mb-2">No courses here</h3>
        <p className="text-muted-foreground mb-4">
          Start learning to see courses in this section
        </p>
        <Link to="/courses">
          <Button className="btn-sketchy">Browse Courses</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {enrollments.map((enrollment) => {
        const course = courses[enrollment.course_id];
        if (!course) return null;

        const isCompleted = !!enrollment.completed_at;

        return (
          <div
            key={enrollment.id}
            className="card-sketchy"
            data-testid={`enrollment-card-${enrollment.id}`}
          >
            <div className="flex gap-4">
              {/* Thumbnail */}
              <div className="w-24 h-24 bg-gray-100 rounded-lg overflow-hidden border-2 border-black flex-shrink-0">
                {course.thumbnail_url ? (
                  <img
                    src={course.thumbnail_url}
                    alt={course.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <BookOpen className="w-8 h-8 text-muted-foreground" />
                  </div>
                )}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-bold text-ink truncate">{course.title}</h3>
                  {isCompleted && (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  )}
                </div>
                <p className="text-sm text-muted-foreground">{course.category}</p>

                {/* Progress */}
                <div className="mt-3">
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-bold text-primary">
                      {Math.round(enrollment.progress)}%
                    </span>
                  </div>
                  <Progress value={enrollment.progress} className="h-2" />
                </div>

                {/* Actions */}
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Started {new Date(enrollment.started_at).toLocaleDateString()}
                  </span>
                  {showContinue && !isCompleted && (
                    <Link to={`/learn/${enrollment.id}`}>
                      <Button size="sm" className="btn-sketchy py-1 px-3" data-testid={`continue-${enrollment.id}`}>
                        <Play className="w-3 h-3 mr-1" />
                        Continue
                      </Button>
                    </Link>
                  )}
                  {isCompleted && (
                    <Link to={`/courses/${course.id}`}>
                      <Button size="sm" variant="outline" className="border-2 border-black py-1 px-3">
                        View Course
                      </Button>
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
